import axios from 'axios';
import { HomeworkConfig, S3PresignedImageResponse, SolutionIdentifier, SolutionDataWithId, SolutionResponse, S3PresignedImageRequest, DeleteHistoryRequest, DashboardData, AccountData, Transaction, MathSolutionStructuredOutput, SolutionGenerationResponse } from '../types';

// In a real production app, this would come from an environment variable.
const BACKEND_URI = process.env.VITE_MATA_API_URL;

// This function will be set by the App component to provide the auth token from Clerk.
let getAuthToken: (() => Promise<string | null>) | null = null;

/**
 * Sets the function that will provide the authentication token.
 * This should be called once from the main App component after Clerk is loaded.
 * @param provider A function that returns a promise resolving to the auth token.
 */
export const setTokenProvider = (provider: () => Promise<string | null>) => {
  getAuthToken = provider;
};

// It's a good practice to create a single axios instance for your API.
// This allows for centralized configuration, like base URLs and error handling.
const apiClient = axios.create({
  baseURL: BACKEND_URI,
});

// Add a request interceptor to automatically add the Authorization header.
apiClient.interceptors.request.use(
  async (config) => {
    if (getAuthToken) {
      const token = await getAuthToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * A centralized utility for handling API errors from Axios.
 * It logs the error and creates a user-friendly Error object.
 * @param error The error object caught, expected to be from Axios.
 * @param defaultMessage A fallback message if the server provides no detail.
 * @returns An `Error` object with a user-friendly message.
 */
const handleApiError = (error: unknown, defaultMessage: string): Error => {
  console.error(defaultMessage, error);
  if (axios.isAxiosError(error) && error.response?.data?.detail) {
    return new Error(error.response.data.detail);
  }
  return new Error(defaultMessage);
};

/**
 * Fetches a shared solution. This endpoint is expected to be public.
 * @param solutionId The ID of the solution to fetch.
 * @returns A promise that resolves with the solution data.
 */
export const getSharedSolution = async (solutionId: string): Promise<SolutionDataWithId> => {

  try {
    // This uses a generic axios instance because it's a public endpoint and doesn't need auth.
    const response = await apiClient.get<SolutionResponse>(`/api/v1/solution/${solutionId}/status`);
    return convertToSolutionDataWithId(response.data);
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch shared solution.');
  }
};

/**
 * Submit shared solution for generating the HTML.
 * @param solutionId The ID of the solution to submit for generation.
 * @returns A promise that resolves with the solution data.
 */
export const startSolutionGeneration = async (solutionId: string): Promise<SolutionGenerationResponse> => {
  try {
    // This uses a generic axios instance because it's a public endpoint and doesn't need auth.
    const response = await apiClient.get<SolutionGenerationResponse>(`/api/v1/share/${solutionId}/create-static`);
    return response.data;
  } catch (error) {
    throw handleApiError(error, 'Failed to fetch shared solution.');
  }
};

/**
 * Ensures a shareable image is generated for a solution and returns the solution details,
 * including the URL to the cached image.
 * @param solutionId The ID of the solution.
 * @returns A promise that resolves with the solution data, including the image URL.
 */
export const ensureShareableImage = async (solutionId: string): Promise<SolutionDataWithId> => {
  try {
    // This endpoint returns the full SolutionResponse object.
    const response = await apiClient.post<SolutionResponse>(`/api/v1/share/${solutionId}/image`);
    return convertToSolutionDataWithId(response.data);
  } catch (error) {
    throw handleApiError(error, 'Failed to generate shareable image link.');
  }
};

const convertToSolutionDataWithId = (response: SolutionResponse): SolutionDataWithId => {
  return {
      id: response.id,
      timestamp: response.timestamp,
      grade: response.grade,
      language: response.language,
      originalImageSrc: response.s3_image_url || '',
      solution_jsonb: response.solution_jsonb,
      s3_solution_image_url: response.s3_solution_image_url,
  };
};


// NOTE: The backend API has an inconsistent response structure. Some endpoints wrap the payload in a `data` object, while others do not. This service handles that inconsistency, but standardizing the backend response would be ideal.

/**
 * Gets a presigned URL from the backend for direct S3 upload.
 * This also creates a "pending" solution record in the database.
 */
export const getPresignedPostUrl = async (file: File, config: HomeworkConfig): Promise<S3PresignedImageResponse> => {
  try {
    const payloadBody: S3PresignedImageRequest = {
        original_image_name: file.name,
        original_image_type: file.type,
        ...config,
    };

    // This endpoint wraps the response in a 'data' object.
    const response = await apiClient.post<{ data: S3PresignedImageResponse }>(
      `/api/v1/generate-upload-url/`,
      payloadBody,
    );
    // The actual payload is nested inside the 'data' property of the response.
    return response.data.data;

  } catch (error) {
    throw handleApiError(error, 'Failed to get presigned URL from server.');
  }
};

/**
 * Uploads a file to S3 using a presigned URL.
 * @returns The full URL of the uploaded object in S3.
 */
export const uploadFileToS3 = async (
  file: File,
  presignedPostData: S3PresignedImageResponse,
  onUploadProgress: (progressEvent: any) => void
): Promise<string> => {
  const formData = new FormData();
  Object.entries(presignedPostData.fields).forEach(([key, value]) => {
    formData.append(key, value as string);
  });
  // The file must be the last field in the form.
  formData.append('file', file);

  try {
    // Use a generic axios post since the URL is for S3, not our API.
    await axios.post(presignedPostData.url, formData, {
      onUploadProgress,
    });
  } catch (error) {
    console.error("S3 upload failed", error);
    throw new Error('Failed to upload file to S3. Please check your network connection and try again.');
  }

  // The final URL is returned from the presigned URL generation step.
  return presignedPostData.image_uri;
};

/**
 * Notifies the backend that the S3 upload is complete.
 */
export const notifyUploadCompleted = async (solutionId: string): Promise<void> => {
    const payload: SolutionIdentifier = { solution_id: solutionId };
    try {
        await apiClient.post(`/api/v1/upload-completed`, payload);
    } catch (error) {
        throw handleApiError(error, 'Failed to notify server of upload completion.');
    }
};

/**
 * Triggers the problem solving process on the backend.
 * This is an ASYNCHRONOUS call that starts the process and returns immediately.
 */
export const startProblemSolving = async (solutionId: string): Promise<void> => {
    const payload: SolutionIdentifier = { solution_id: solutionId };
    try {
        await apiClient.post(`/api/v1/solve-problem`, payload);
    } catch (error) {
        throw handleApiError(error, 'Failed to start the solving process.');
    }
};

/**
 * Fetches the status of a solution from the backend.
 */
const getSolutionStatus = async (solutionId: string): Promise<SolutionResponse> => {
    // This endpoint does NOT wrap the response in a 'data' object.
    try {
        const response = await apiClient.get<SolutionResponse>(`/api/v1/solution/${solutionId}/status`);
        return response.data; // The data directly matches the SolutionResponse type
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 404) {
            throw new Error('Solution not found. It may have expired or the link is incorrect.');
        }
        throw handleApiError(error, 'Could not retrieve solution status.');
    }
}

/**
 * Polls the backend until the solution is ready (completed or failed).
 * @param solutionId The ID of the solution to poll for.
 * @returns A promise that resolves with the final solution data.
 */
export const pollForSolutionResult = async (solutionId: string): Promise<SolutionDataWithId> => {
    const MAX_POLLS = 5; // 60 polls * 2 seconds = 2 minute timeout
    const POLL_INTERVAL = 2000; // 2 seconds

    for (let i = 0; i < MAX_POLLS; i++) {
        try {
            const backendSolution = await getSolutionStatus(solutionId); // This now throws a clean, user-friendly error.

            if (backendSolution.status === 'COMPLETED') {
                return convertToSolutionDataWithId(backendSolution);
            } else if (backendSolution.status === 'FAILED' || backendSolution.status === 'DELETED') {
                console.error(`Solution ${solutionId} processing failed on the server.`);
                throw new Error('The server failed to process the image. Please try a different image or contact support.');
            }

            // If status is still PENDING or PROCESSING, wait for the next poll.
            await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL));
        } catch (error) {
            // Errors from getSolutionStatus or the 'FAILED' case are already user-friendly.
            // We just need to re-throw them to be caught by the calling UI component.
            throw error; // Re-throw the error to be caught by the calling function.
        }
    }
    throw new Error('The request timed out. The server may be busy, please try again later.');
};

/**
 * Fetches dashboard data from the backend.
 */
export const getDashboardData = async (): Promise<DashboardData> => {
  try {
    // The backend returns a structure where recentSolutions are of type SolutionResponse[], not SolutionDataWithId[]
    const response = await apiClient.get<DashboardData>(`/api/v1/dashboard/overview`);
    const apiData = response.data;
    return {
      ...apiData,
      // We convert recentSolutions to add the originalImageSrc for the UI
      recentSolutions: (apiData.recentSolutions as unknown as SolutionResponse[]).map(convertToSolutionDataWithId),
    };
  } catch (error) {
    throw handleApiError(error, "Failed to fetch dashboard data.");
  }
};

/**
 * Fetches account data from the backend.
 */
export const getAccountData = async (): Promise<AccountData> => {
  try {
    // The backend returns `memberSince` as a Unix timestamp (number).
    // We need to convert it to a localized date string for the UI.
    const response = await apiClient.get<{
      email: string;
      username: string;
      memberSince: number;
      credits: number;
    }>(`/api/v1/dashboard/account`);

    const apiData = response.data;
    return {
      ...apiData,
      memberSince: new Date(apiData.memberSince * 1000).toLocaleDateString('sr-RS'),
    };
  } catch (error) {
    throw handleApiError(error, "Failed to fetch account data.");
  }
};

/**
 * Fetches transaction history from the backend.
 */
export const getTransactionHistory = async (): Promise<Transaction[]> => {
    try {
        const response = await apiClient.get<Transaction[]>(`/api/v1/dashboard/transactions`);
        return response.data;
    } catch (error) {
        throw handleApiError(error, "Failed to fetch transaction history.");
    }
};

/**
 * Fetches history from the backend.
 */
export const getHistory = async (
  page: number,
  limit: number
): Promise<{ items: SolutionDataWithId[], hasMore: boolean }> => {
  const start = page * limit;
  try {
    const response = await apiClient.get<{ data: SolutionResponse[] }>(
      `/api/v1/problems/history`,
      { params: { start, limit } }
    );
    const items = response.data.data.map(convertToSolutionDataWithId);
    const hasMore = items.length === limit;
    return { items, hasMore };
  } catch (error) {
    throw handleApiError(error, "Failed to fetch history.");
  }
};

/**
 * Deletes a single history item from the backend.
 */
export const deleteSingleHistoryItem = async (solutionId: string): Promise<void> => {
  try {
    await apiClient.delete(`/api/v1/problems/history/${solutionId}`);
  } catch (error) {
    throw handleApiError(error, "Failed to delete history item.");
  }
};

/**
 * Deletes multiple history items from the backend.
 */
export const deleteMultipleHistoryItems = async (solutionIds: string[]): Promise<void> => {
  try {
    const payload: DeleteHistoryRequest = { solution_ids: solutionIds };
    // For DELETE with body, axios requires the payload to be in the `data` property of the config object
    await apiClient.delete(`/api/v1/problems/history`, { data: payload });
  } catch (error) {
    throw handleApiError(error, "Failed to delete history items.");
  }
};

/**
 * Clears all history on the backend.
 */
export const clearApiHistory = async (): Promise<void> => {
  try {
    // No payload means clear all
    await apiClient.delete(`/api/v1/problems/history`);
  } catch (error) {
    throw handleApiError(error, "Failed to clear all history.");
  }
};

/**
 * Notifies the backend that a payment was completed successfully.
 * @param transactionId The transaction ID from Paddle.
 * @param solutionId An optional solution ID to associate with the payment.
 * @returns A promise that resolves when the notification is sent.
 */
export const notifyPaymentCompleted = async (transactionId: string, solutionId?: string): Promise<void> => {

  // Wait for 3 seconds before sending the notification to the backend.
  await new Promise(resolve => setTimeout(resolve, 1000));

  const payload: { transaction_id: string; solution_id?: string } = {
    transaction_id: transactionId,
  };
  if (solutionId) {
    payload.solution_id = solutionId;
  }
  try {
    await apiClient.post(`/api/v1/transactions/verify-payment-completed`, payload);
  } catch (error) {
    throw handleApiError(error, 'Failed to notify server of payment completion.');
  }
};